import { fsConfig, MY_COMPUTER_PATH } from '@/config/filesystem';
import type { FsFolder, FsNode, FsNodeInput } from './types';

/**
 * Turns the hand-authored config into a real tree: computes every `path`,
 * derives `ext` from the file name, and indexes the lot by lowercased path so
 * lookups are O(1) and case-insensitive (as they were on FAT/NTFS).
 */

function extOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(dot).toLowerCase() : '';
}

function joinPath(parent: string, name: string): string {
  if (!parent) return name;
  return parent.endsWith('\\') ? `${parent}${name}` : `${parent}\\${name}`;
}

function build(input: FsNodeInput, parentPath: string): FsNode {
  const path = joinPath(parentPath, input.name);

  if (input.type === 'folder') {
    const folder: FsFolder = {
      ...input,
      path,
      children: [],
    };
    folder.children = input.children.map((child) => build(child, path));
    return folder;
  }

  return {
    ...input,
    path,
    ext: input.ext?.toLowerCase() ?? extOf(input.name),
  };
}

/** The C: drive, fully resolved. */
const drive = build(fsConfig, '') as FsFolder;

/**
 * "My Computer" is a virtual root above the drives — it isn't a real
 * directory, which is why it's synthesised here rather than in the config.
 */
export const myComputer: FsFolder = {
  type: 'folder',
  name: 'My Computer',
  path: MY_COMPUTER_PATH,
  special: 'my-computer',
  icon: '/assets/icons/my-computer.svg',
  comment: 'Everything on this computer',
  children: [drive],
};

export const root = myComputer;

/** path (lowercased) → node */
const index = new Map<string, FsNode>();
/** child path (lowercased) → parent folder */
const parents = new Map<string, FsFolder>();

function indexNode(node: FsNode, parent: FsFolder | undefined): void {
  index.set(node.path.toLowerCase(), node);
  if (parent) parents.set(node.path.toLowerCase(), parent);
  if (node.type === 'folder') {
    for (const child of node.children) indexNode(child, node);
  }
}

indexNode(myComputer, undefined);

export function lookup(lowerPath: string): FsNode | undefined {
  return index.get(lowerPath);
}

export function lookupParent(lowerPath: string): FsFolder | undefined {
  return parents.get(lowerPath);
}

/** Every node in the tree — used by search and by tests. */
export function allNodes(): FsNode[] {
  return [...index.values()];
}

/* -------------------------------------------------------------------------- */
/* Moving things                                                              */
/* -------------------------------------------------------------------------- */

function unindexNode(node: FsNode): void {
  const key = node.path.toLowerCase();
  index.delete(key);
  parents.delete(key);
  if (node.type === 'folder') {
    for (const child of node.children) unindexNode(child);
  }
}

/** Rewrite `node`'s path, and every path beneath it, to hang off `path`. */
function repath(node: FsNode, path: string): void {
  node.path = path;
  if (node.type === 'folder') {
    for (const child of node.children) repath(child, joinPath(path, child.name));
  }
}

/**
 * Move `node` into `dest`, for real: it leaves its old parent's `children`,
 * joins the new one's, and every path in its subtree is rewritten to match.
 *
 * A move *is* a path change, and the two indexes are keyed by path — so the
 * old keys have to come out before the rewrite and the new ones go in after.
 * Doing it in that order is the whole of the bookkeeping, and the reason this
 * lives next to `indexNode` rather than in the layer above.
 *
 * Nothing here validates: `src/fs/mutate.ts` owns the question of whether a
 * given move is allowed, and calls this only once the answer is yes.
 */
export function relocate(node: FsNode, dest: FsFolder): void {
  const from = parents.get(node.path.toLowerCase());
  if (from) {
    const at = from.children.indexOf(node);
    if (at !== -1) from.children.splice(at, 1);
  }

  unindexNode(node);
  dest.children.push(node);
  repath(node, joinPath(dest.path, node.name));
  indexNode(node, dest);
}

/**
 * Put the disk back the way it was authored. Moves live only in memory — a
 * reload has always been this computer's undo — so nothing in the shell calls
 * this; it is here so a test can move things without leaking into the next one.
 */
export function resetTree(): void {
  index.clear();
  parents.clear();
  myComputer.children = [build(fsConfig, '') as FsFolder];
  indexNode(myComputer, undefined);
}
